import { AppRoutes } from "@/constants/routes";
import { getSucursales, Sucursal } from "@/services/accounts.service";
import { getFertilizantes } from "@/services/products.service";
import {
  createFertSlotRequestOrders,
  CrearPedidosSolicitudBody,
} from "@/services/fert-slot-requests.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { toaster } from "@/components/ui/toaster";
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";
import { UseFieldArrayReplace } from "react-hook-form";
import { FertSlotRequestOrderCreationForm } from "./fert-slot-request-order-creation.schema";

export interface CreateOrdersHandlerProps {
  id: string | undefined;
  values: FertSlotRequestOrderCreationForm;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export interface OrderFormProduct {
  productId?: string | string[];
  tons?: string | number;
}

export interface OrderFormItem {
  dispatchPlace?: string | string[];
  observations?: string;
  URLMaps?: string;
  products?: OrderFormProduct[];
}

export const handleCancelHandler = (
  navigate: NavigateFunction,
  id: string | undefined,
) => {
  if (id) {
    navigate(AppRoutes.FERT_SLOTS_REQ_DETAIL.replace(":id", String(id)));
    return;
  }
  navigate(AppRoutes.FERT_SLOTS_REQ);
};

export const fetchProductsHandler = async (params: {
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setLoadingProducts: (value: boolean) => void;
  setFertilizantes: (value: { id: number; nombre: string }[]) => void;
}) => {
  const { navigate, fetchPermissions, setLoadingProducts, setFertilizantes } = params;
  try {
    setLoadingProducts(true);
    const response = await getFertilizantes({ activo: true });

    const mapped = (response ?? []).map((p: any) => ({
      id: Number(p?.id),
      nombre: String(p?.nombre ?? ""),
    }));
    setFertilizantes(mapped);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.FERT_SLOTS_REQ,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "No se pudieron cargar los fertilizantes.",
        ),
      });
    }

    setFertilizantes([]);
  } finally {
    setLoadingProducts(false);
  }
};

export const fetchBranchesHandler = async (params: {
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  setLoadingBranches: (value: boolean) => void;
  setSucursales: (value: Sucursal[]) => void;
}) => {
  const { navigate, fetchPermissions, setLoadingBranches, setSucursales } = params;
  try {
    setLoadingBranches(true);
    const response = await getSucursales();
    setSucursales((response ?? []) as Sucursal[]);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.FERT_SLOTS_REQ,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "No se pudieron cargar las sucursales.",
        ),
      });
    }

    setSucursales([]);
  } finally {
    setLoadingBranches(false);
  }
};

export const buildProductOptions = (
  fertilizantes: { id: number; nombre: string }[],
  extraProducts?: { id: number; nombre: string }[],
) => {
  const active = fertilizantes ?? [];
  const extras = extraProducts ?? [];

  const activeIds = new Set(active.map((p) => Number(p.id)).filter(Number.isFinite));

  const activeById = new Map<number, { id: number; nombre: string }>();
  for (const p of active) {
    const id = Number(p?.id);
    if (!Number.isFinite(id)) continue;
    if (!activeById.has(id)) {
      activeById.set(id, { id, nombre: String(p?.nombre ?? "") });
    }
  }

  const inactiveById = new Map<number, { id: number; nombre: string }>();
  for (const p of extras) {
    const id = Number(p?.id);
    if (!Number.isFinite(id)) continue;
    if (activeIds.has(id)) continue;
    if (!inactiveById.has(id)) {
      inactiveById.set(id, { id, nombre: String(p?.nombre ?? "") });
    }
  }

  const activeOptions = [...activeById.values()].map((p) => ({
    label: p.nombre,
    value: String(p.id),
    isDisabled: false,
  }));

  const inactiveOptions = [...inactiveById.values()].map((p) => ({
    label: `${p.nombre} (Inactivo)`,
    value: String(p.id),
    isDisabled: true,
  }));

  return [...activeOptions, ...inactiveOptions];
};

export const buildDispatchPlaceOptions = (sucursales: Sucursal[]) =>
  sucursales.map((s) => ({
    label: s.nombre,
    value: s.clave,
  }));

export const handleDeleteOrderHandler = (params: {
  index: number;
  removeOrder: (index: number) => void;
  setEditingIndex: (updater: (prev: number) => number) => void;
}) => {
  const { index, removeOrder, setEditingIndex } = params;

  removeOrder(index);
  setEditingIndex((prev) => {
    if (prev === index) return Math.max(0, index - 1);
    if (prev > index) return prev - 1;
    return prev;
  });
};

export const handleSaveAndCreateAnotherOrderHandler = (params: {
  appendOrder: (value: any) => void;
  setEditingIndex: (value: number) => void;
  ordersLength: number;
}) => {
  const { appendOrder, setEditingIndex, ordersLength } = params;

  appendOrder({
    dispatchPlace: "",
    observations: "",
    products: [{ productId: "", tons: "" }],
  });
  setEditingIndex(ordersLength);
};

export const createOrdersHandler = async ({
  id,
  values,
  navigate,
  fetchPermissions,
}: CreateOrdersHandlerProps) => {
  try {
    const normalizeSingle = (v: any) => {
      if (Array.isArray(v)) return v?.[0];
      return v;
    };

    const body: CrearPedidosSolicitudBody = {
      pedidos: (values?.orders ?? []).map((o: OrderFormItem) => ({
        lugarDespacho: String(normalizeSingle(o?.dispatchPlace) ?? ""),
        URLMaps: String(o?.URLMaps ?? ""),
        observaciones: String(o?.observations ?? ""),
        productos: (o?.products ?? []).map((p: OrderFormProduct) => {
          const raw = typeof p?.tons === "string" ? p.tons : String(p?.tons ?? "");
          const trimmed = raw.trim();
          let parsed = 0;
          if (trimmed) {
            parsed = Number.parseFloat(trimmed.replace(",", "."));
          }
          return {
            idProducto: Number(normalizeSingle(p?.productId)),
            cantidad: Number.isFinite(parsed) ? parsed : 0,
          };
        }),
      })),
    };

    await createFertSlotRequestOrders(Number(id), body);

    toaster.create({
      type: "success",
      title: "Pedidos creados exitosamente.",
    });

    navigate(AppRoutes.FERT_SLOTS_REQ_DETAIL.replace(":id", String(id)));
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.FERT_SLOTS_REQ_DETAIL.replace(":id", String(id)),
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(
          error as AxiosError,
          "No se pudieron crear los pedidos.",
        ),
      });
    }

    throw error;
  }
};

export interface PreloadProductsFromDetailProps {
  detail: any;
  didPreloadFromDetail: any;
  fertilizantes: any[];
  replaceOrders:  UseFieldArrayReplace<FertSlotRequestOrderCreationForm>;
}

export const preloadProductsFromDetail = ({ detail, didPreloadFromDetail, fertilizantes, replaceOrders }: PreloadProductsFromDetailProps) => {
    if (!detail || didPreloadFromDetail.current) return;
    if (!fertilizantes?.length) return;

    const normalizeName = (s: any) =>
      String(s ?? "")
        .trim()
        .toLowerCase();

    const fertilizanteIdByName = new Map(
      fertilizantes.map((f) => [normalizeName(f.nombre), f.id]),
    );
    const fertilizanteIds = new Set(fertilizantes.map((f) => String(f.id)));

    const products = (detail.productos ?? []).map((p: any) => {
      const rawId = p?.idProducto ?? p?.id;
      const rawName = p?.nombre ?? p?.nombreProducto;
      const idAsString = rawId !== undefined && rawId !== null ? String(rawId) : "";

      const byId = idAsString && fertilizanteIds.has(idAsString) ? idAsString : "";
      const byRawId = idAsString;
      const byName = fertilizanteIdByName.get(normalizeName(rawName));
      const finalId =
        byId ||
        (byName === undefined ? "" : String(byName)) ||
        byRawId;

      return {
        productId: finalId ? [finalId] : [],
        tons: String(p?.cantidad ?? ""),
      };
    });

    if (!products.length) return;

    replaceOrders([
      {
        dispatchPlace: "",
        observations: "",
        products,
      },
    ]);

    didPreloadFromDetail.current = true;
}